import { Controller } from "@hotwired/stimulus"

function csrfToken() {
    const m = document.querySelector('meta[name="csrf-token"]')
    return m ? m.getAttribute("content") : ""
}
const headers = () => ({ "X-Requested-With": "XMLHttpRequest", "X-CSRF-Token": csrfToken() })
const rowId = (row) => (row?.dataset?.id || "").toString()

export default class extends Controller {
    static targets = ["counter", "applyBtn", "keepModal", "keepForm", "keepCount", "keepKm"]
    static values = { mode: { type: String, default: "review" } } // "review" | "keep" | "delete"

    connect() {
        this.selected = new Set()
        this._setMode("review")
    }

    setMode(event) {
        let mode = event.currentTarget?.dataset?.mode || "review"
        if (this.modeValue === mode && mode !== "review") mode = "review"
        this._setMode(mode)

        const seg = event.currentTarget?.closest(".k-dock") || event.currentTarget?.closest(".k-seg")
        if (seg) {
            seg.querySelectorAll(".k-seg-btn").forEach(b => {
                const btnMode = b.dataset.mode
                b.classList.toggle("is-active", btnMode === mode)
            })
        }
    }

    toggleSelect(e) {
        if (this.modeValue === "review") return
        const row = e.currentTarget.closest?.("[data-id]") || e.currentTarget
        if (!row || !row.dataset) return

        const id = rowId(row)
        if (!id) return

        if (this.selected.has(id)) this.selected.delete(id)
        else this.selected.add(id)

        row.classList.toggle("is-selected", this.selected.has(id))
        this._updateCounter()
    }

    apply() {
        const ids = Array.from(this.selected)
        if (this.modeValue === "review" || ids.length === 0) return

        if (this.modeValue === "keep") {
            this._openKeepModal()
        } else if (this.modeValue === "delete") {
            this._runDelete(ids)
        }
    }

    // ━━━ KEEP MODAL ACTIONS ━━━

    _openKeepModal() {
        if (this.hasKeepModalTarget) {
            this.keepModalTarget.style.display = "grid"

            // Calculate stats
            const ids = Array.from(this.selected)
            this.keepCountTarget.textContent = ids.length

            let totalKm = 0
            this.element.querySelectorAll(".t-row.is-selected").forEach(row => {
                totalKm += parseFloat(row.dataset.km || 0)
            })
            this.keepKmTarget.textContent = totalKm.toFixed(1)

            // Focus first input
            setTimeout(() => this.keepFormTarget.querySelector("input")?.focus(), 100)

            // Pre-fill logic
            const fromInput = this.keepFormTarget.querySelector('[name="from_label"]')
            const toInput = this.keepFormTarget.querySelector('[name="to_label"]')

            if (ids.length === 1) {
                // Find the single selected row
                const row = this.element.querySelector(`.t-row[data-id="${ids[0]}"]`)
                if (row) {
                    if (fromInput) fromInput.value = row.dataset.from || ""
                    if (toInput) toInput.value = row.dataset.to || ""
                }
            } else {
                // Multiple selection: clear specific overrides
                if (fromInput) fromInput.value = ""
                if (toInput) toInput.value = ""
            }
        }
    }

    closeKeepModal() {
        if (this.hasKeepModalTarget) this.keepModalTarget.style.display = "none"
    }

    async confirmKeep(e) {
        e.preventDefault()
        const ids = Array.from(this.selected)
        if (ids.length === 0) return

        const formData = new FormData(this.keepFormTarget)
        ids.forEach(id => formData.append("ids[]", id))

        try {
            await fetch("/trips/bulk_keep", {
                method: "POST",
                body: formData,
                headers: headers()
            })

            this.closeKeepModal()
            this.keepFormTarget.reset()
            this._markAs(ids, "kept")
            this.selected.clear()
            this._updateCounter()

        } catch (err) {
            console.error(err)
            alert("Une erreur est survenue.")
        }
    }

    // ━━━ DELETE ACTIONS ━━━

    async _runDelete(ids) {
        const body = new FormData()
        ids.forEach(id => body.append("ids[]", id))
        await fetch("/trips/bulk_delete", { method: "POST", body, headers: headers() })
        this._markAs(ids, "discarded")
        this.selected.clear()
        this._updateCounter()
    }

    // ━━━ HELPERS ━━━

    _markAs(ids, status) {
        this.element.querySelectorAll(".t-row").forEach(row => {
            if (ids.includes(rowId(row))) {
                row.classList.remove("is-selected")
                if (status === "kept") row.classList.add("is-kept")
                if (status === "discarded") row.classList.add("is-discarded")
            }
        })
    }

    // Legacy support if needed, but we use _markAs now
    _removeRows(ids) { this._markAs(ids, "discarded") }

    async accept(e) { await this._postProp(e) }
    async reject(e) { await this._postProp(e) }
    async _postProp(e) {
        const btn = e.currentTarget
        const url = btn.dataset.url
        const row = btn.closest(".k-prop")
        if (!url || !row) return
        row.classList.add("is-leaving")
        try {
            await fetch(url, { method: "POST", headers: headers() })
            setTimeout(() => row.remove(), 200)
        } catch {
            row.classList.remove("is-leaving")
        }
    }

    async restore(e) {
        e.stopPropagation()
        const btn = e.currentTarget
        const url = btn.dataset.url
        const row = btn.closest(".t-row")

        if (!url || !row) return

        // Spin icon?
        btn.querySelector("wa-icon").name = "spinner" // if using FA strings, or just assume instant

        await fetch(url, { method: "POST", headers: headers() })

        row.classList.remove("is-discarded")
        // The CSS will handle showing/hiding the button vs selector if I structure it right,
        // otherwise for now since I rendered conditionally in ERB, I MUST reload.
        // Reloading is safest for now given previous step used 'if/else' in ERB.
        window.location.reload()
    }

    _setMode(mode) {
        this.modeValue = mode
        document.documentElement.setAttribute("data-mode", mode)
        this.element.querySelectorAll(".t-row.is-selected").forEach(r => r.classList.remove("is-selected"))
        this.selected = new Set()
        this._updateCounter()
    }

    _updateCounter() {
        if (this.hasCounterTarget) this.counterTarget.textContent = this.selected.size
        if (this.hasApplyBtnTarget) this.applyBtnTarget.disabled = (this.modeValue === "review" || this.selected.size === 0)
    }
}